/**
 * Premium Client-Side Wishlist persistence manager
 */

class WishlistManager {
  constructor() {
    this.storageKey = 'jarvis_wishlist';
    this.wishlist = this.loadWishlist();
    
    this.grid = document.querySelector('[data-wishlist-grid]');
    this.emptyState = document.querySelector('[data-wishlist-empty]');

    this.init();
  }

  init() {
    this.syncGlobalHearts();
    this.bindEvents();

    if (this.grid) {
      this.renderWishlistPage();
    }
  }

  loadWishlist() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      console.error('[Wishlist] Failed to parse localStorage items:', e);
      return [];
    }
  }

  saveWishlist() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlist));
    } catch (e) {
      console.error('[Wishlist] Failed to write to localStorage:', e);
    }
  }

  syncGlobalHearts() {
    document.querySelectorAll('[data-wishlist-button]').forEach(btn => {
      const handle = btn.getAttribute('data-product-handle');
      if (handle && this.wishlist.includes(handle)) {
        btn.classList.add('is-active');
        const svg = btn.querySelector('svg');
        if (svg) svg.setAttribute('fill', 'currentColor');
      } else {
        btn.classList.remove('is-active');
        const svg = btn.querySelector('svg');
        if (svg) svg.setAttribute('fill', 'none');
      }
    });

    // Sync global header badge count
    const badge = document.getElementById('WishlistCountBadgeGlobal');
    if (badge) {
      const count = this.wishlist.length;
      badge.textContent = count;
      if (count > 0) {
        badge.classList.remove('hidden');
      } else {
        badge.classList.add('hidden');
      }
    }
  }

  bindEvents() {
    // Dynamic click handler via document-level event delegation
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wishlist-button]');
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      const handle = btn.getAttribute('data-product-handle');
      if (!handle) return;

      this.toggleWishlistItem(handle, btn);
    });
  }

  toggleWishlistItem(handle, clickedBtn) {
    const index = this.wishlist.indexOf(handle);
    let added = false;

    if (index > -1) {
      // Remove item
      this.wishlist.splice(index, 1);
    } else {
      // Add item
      this.wishlist.push(handle);
      added = true;
    }

    // Trigger visual pop/wiggle heartbeat animation if clicked directly
    if (clickedBtn) {
      clickedBtn.classList.add('wishlist-btn-pop');
      clickedBtn.addEventListener('animationend', () => {
        clickedBtn.classList.remove('wishlist-btn-pop');
      }, { once: true });
    }

    this.saveWishlist();
    this.syncGlobalHearts();

    // Trigger visual toast popup when added
    if (added) {
      let productTitle = 'Product';
      const card = clickedBtn.closest('.product-card');
      if (card) {
        const titleEl = card.querySelector('.product-card__title a') || card.querySelector('.product-card__title');
        if (titleEl) productTitle = titleEl.textContent.trim();
      } else {
        const pageTitleEl = document.querySelector('.product-info__title');
        if (pageTitleEl) productTitle = pageTitleEl.textContent.trim();
      }
      this.showToast(productTitle);
    }

    // Trigger visual changes on active wishlist grids
    if (this.grid) {
      const card = clickedBtn.closest('.product-card');
      if (card) {
        card.classList.add('is-removing');
        setTimeout(() => {
          card.remove();
          if (this.wishlist.length === 0) {
            this.showEmptyState();
          }
        }, 400);
      }
    }
  }

  showToast(productTitle) {
    // Remove existing toast first
    const existing = document.querySelector('.wishlist-toast');
    if (existing) {
      existing.remove();
    }

    const toast = document.createElement('div');
    toast.className = 'wishlist-toast';
    toast.innerHTML = `
      <div class="wishlist-toast__icon">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
          <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
        </svg>
      </div>
      <div class="wishlist-toast__content">
        <span class="wishlist-toast__label">ADDED TO WISHLIST</span>
        <span class="wishlist-toast__title">${productTitle}</span>
      </div>
      <a href="/search?view=wishlist" class="wishlist-toast__action">VIEW</a>
      <button class="wishlist-toast__close" aria-label="Dismiss">&times;</button>
    `;

    document.body.appendChild(toast);

    // Trigger reflow to initiate CSS transition
    toast.offsetHeight;
    toast.classList.add('is-visible');

    // Close on click close button
    const closeBtn = toast.querySelector('.wishlist-toast__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.dismissToast(toast);
      });
    }

    // Auto dismiss after 3.5s
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
    }
    this.toastTimeout = setTimeout(() => {
      this.dismissToast(toast);
    }, 3500);
  }

  dismissToast(toast) {
    if (!toast || !toast.parentNode) return;
    toast.classList.remove('is-visible');
    setTimeout(() => {
      if (toast.parentNode) {
        toast.remove();
      }
    }, 400);
  }

  showEmptyState() {
    if (this.grid) this.grid.classList.add('hidden');
    if (this.emptyState) this.emptyState.classList.remove('hidden');
  }

  async renderWishlistPage() {
    if (this.wishlist.length === 0) {
      this.showEmptyState();
      return;
    }

    const promises = this.wishlist.map(handle => {
      return fetch(`/products/${handle}.js`)
        .then(response => {
          if (!response.ok) throw new Error(`Product ${handle} not found`);
          return response.json();
        })
        .catch(err => {
          console.warn('[Wishlist] Fetch error:', err);
          return null;
        });
    });

    const results = await Promise.all(promises);
    const validProducts = results.filter(product => product !== null);

    if (validProducts.length === 0) {
      this.showEmptyState();
      return;
    }

    // Render cards
    let gridHtml = '';
    validProducts.forEach(product => {
      gridHtml += this.buildProductCardHtml(product);
    });

    this.grid.innerHTML = gridHtml;
    
    // Synchronize hearts inside newly rendered grid cards
    this.syncGlobalHearts();

    // Re-initialize add-to-cart button text sliding animations
    if (typeof window.initStoreButtons === 'function') {
      window.initStoreButtons();
    }
  }

  buildProductCardHtml(product) {
    const price = (product.price / 100).toFixed(2);
    const compareAtPrice = product.compare_at_price ? (product.compare_at_price / 100).toFixed(2) : null;
    const compareAtPriceText = compareAtPrice ? `<span class="product-card__price product-card__price--compare">LE ${compareAtPrice}</span>` : '';
    const saleBadge = compareAtPrice ? `<span class="product-card__sale-badge">${Math.round((product.compare_at_price - product.price) / product.compare_at_price * 100)}% OFF</span>` : '';
    
    const variantId = product.variants[0] ? product.variants[0].id : '';

    // Extract potential badges from tags
    let badgeLabel = '';
    if (product.tags) {
      const tagsLower = product.tags.map(t => t.toLowerCase());
      if (tagsLower.includes('restocking')) {
        badgeLabel = 'Restocking';
      }
    }
    
    const badgeClass = 'product-card__badge--restocking';
    const badgeHtml = badgeLabel ? `<span class="product-card__badge ${badgeClass}">${badgeLabel}</span>` : '';
    
    // Safe fallback image
    const imageSrc = product.featured_image ? product.featured_image : '//cdn.shopify.com/s/images/themes/product-1.png';

    return `
      <div class="product-card">
        <div class="product-card__image-container">
          ${badgeHtml}
          ${compareAtPrice ? `<span class="product-card__discount-badge">-${Math.round((product.compare_at_price - product.price) / product.compare_at_price * 100)}%</span>` : ''}
          <a href="${product.url}" class="product-card__image-link" aria-label="View ${product.title}">
            <img src="${imageSrc}" alt="${product.title}" loading="lazy" class="product-card__image">
          </a>
        </div>
        <div class="product-card__info">
          <span class="product-card__vendor">${product.vendor}</span>
          <h3 class="product-card__title">
            <a href="${product.url}">${product.title}</a>
          </h3>
          <div class="product-card__price-wrapper">
            <span class="product-card__price">LE ${price}</span>
            ${compareAtPriceText}
          </div>
          ${saleBadge}
          <div class="product-card__actions">
            <button type="button" class="product-card__button" data-variant-id="${variantId}" aria-label="Add to cart">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" aria-hidden="true" focusable="false">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.625 10.5a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm7.5 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
              </svg>
              <span class="product-card__button-text">Add to Cart</span>
            </button>
            <button type="button" class="product-card__wishlist-desktop" aria-label="Add to wishlist" data-product-handle="${product.handle}" data-wishlist-button>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true" focusable="false">
                <path stroke-linecap="round" stroke-linejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    `;
  }
}

// Instantiate manager on DOM load
document.addEventListener('DOMContentLoaded', () => {
  window.wishlistManager = new WishlistManager();
});
